import React from "react";
import { Link, useLocation } from "react-router-dom";
import {
  Activity,
  LogOut,
  Users,
  LayoutDashboard,
  Factory,
  Workflow as WorkflowIcon,
  Contact,
  ClipboardList,
  FileText,
  Receipt,
  ListChecks,
  MonitorPlay,
  Tv,
  Package,
  Layers,
  Truck,
  ShoppingCart,
  Settings,
  Wallet,
  BookOpen,
  Boxes,
  PackagePlus,
  Shirt,
  PackageCheck,
  BarChart3,
  Star,
  Wrench,
  Image,
  History,
  ClipboardCheck,
  Coins,
  MessageCircle,
  Smartphone,
  CalendarX,
  KeyRound,
  LifeBuoy,
  Tag,
  Menu,
  X,
  ChevronDown,
  ChevronsLeft,
  ChevronsRight,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Logo, AppCredit } from "@/components/Logo";
import Lonceng from "@/components/Lonceng";
import { labelPeran } from "@/lib/roles";
import { DASHBOARD_ITEM, menuForRole, menuForUser, activeGroupId } from "@/lib/menu";

const ICONS = {
  dashboard: LayoutDashboard, cart: ShoppingCart, shirt: Shirt, wrench: Wrench, image: Image,
  contact: Contact, activity: Activity, receipt: Receipt, clipboard: ClipboardList, wallet: Wallet,
  packageCheck: PackageCheck, listChecks: ListChecks, history: History, fileText: FileText,
  monitor: MonitorPlay, coins: Coins, tv: Tv, boxes: Boxes, packagePlus: PackagePlus, book: BookOpen,
  chart: BarChart3, package: Package, layers: Layers, truck: Truck, users: Users, factory: Factory,
  workflow: WorkflowIcon, clipboardCheck: ClipboardCheck, settings: Settings, message: MessageCircle, phone: Smartphone, calendar: CalendarX, tag: Tag, star: Star,
};

const STORE_MINI = "soqprint.sidebar.mini";
const STORE_CLOSED = "soqprint.sidebar.closedGroups";

const readStore = (key, fallback) => {
  try {
    const v = window.localStorage.getItem(key);
    return v === null ? fallback : JSON.parse(v);
  } catch {
    return fallback;
  }
};
const writeStore = (key, value) => {
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* storage unavailable: preference simply not remembered */
  }
};

export const Brand = ({ compact = false }) =>
  compact ? (
    <img src="/logo/ikon-48.png" alt="SOQPRINT" className="h-9 w-9 rounded-md" data-testid="logo-ikon" />
  ) : (
    <div className="min-w-0">
      <Logo varian="putih" className="h-7 w-auto" />
      <span className="mt-1 block font-judul text-[13px] uppercase tracking-wide text-white">
        Produksi &amp; Tracking SPK
      </span>
    </div>
  );

const NavLinkItem = ({ item, active, mini, onNavigate }) => {
  const Icon = ICONS[item.icon] || Activity;
  const cls = `flex items-center gap-2.5 rounded-md px-2.5 py-2 text-sm transition-colors border ${
    active
      ? "bg-soq-oranye text-soq-biru-tua font-semibold border-soq-oranye"
      : "text-[#E8EBFF] hover:text-white hover:bg-white/10 border-transparent"
  } ${mini ? "justify-center" : ""}`;
  const body = (
    <>
      <Icon size={16} className="shrink-0" />
      {!mini && <span className="truncate">{item.label}</span>}
    </>
  );
  if (item.external) {
    return (
      <a href={item.to} target="_blank" rel="noopener noreferrer" data-testid={item.testId}
        title={item.label} className={cls} onClick={onNavigate}>
        {body}
      </a>
    );
  }
  return (
    <Link to={item.to} data-testid={item.testId} title={item.label} className={cls} onClick={onNavigate}>
      {body}
    </Link>
  );
};

const NavList = ({ role, user, pathname, mini = false, onNavigate }) => {
  const groups = user ? menuForUser(user) : menuForRole(role);  // v2.15: + Layar produksi when the account may see it
  const activeGroup = activeGroupId(groups, pathname);
  const [closed, setClosed] = React.useState(() => readStore(STORE_CLOSED, []));
  const toggle = (id) => {
    const next = closed.includes(id) ? closed.filter((x) => x !== id) : [...closed, id];
    setClosed(next);
    writeStore(STORE_CLOSED, next);
  };
  const isActive = (to) => pathname === to;

  return (
    <nav data-testid="side-nav" className="space-y-3">
      <NavLinkItem item={DASHBOARD_ITEM} active={isActive("/")} mini={mini} onNavigate={onNavigate} />
      {groups.map((g) => {
        // The group of the open page always stays expanded.
        const open = mini || g.id === activeGroup || !closed.includes(g.id);
        return (
          <div key={g.id} data-testid={`nav-group-${g.id}`}>
            {mini ? (
              <div className="mx-2 border-t border-white/20 mb-2" />
            ) : (
              <button
                type="button"
                onClick={() => toggle(g.id)}
                data-testid={`nav-group-toggle-${g.id}`}
                className="w-full flex items-center justify-between px-2.5 py-1 text-[11px] font-semibold uppercase tracking-widest text-[#C9CEFF] hover:text-white"
              >
                <span>{g.label}</span>
                <ChevronDown size={12} className={`transition-transform ${open ? "" : "-rotate-90"}`} />
              </button>
            )}
            {open && (
              <div className="space-y-0.5 mt-1">
                {g.items.map((it) => (
                  <NavLinkItem key={it.to + it.testId} item={it} active={isActive(it.to)} mini={mini} onNavigate={onNavigate} />
                ))}
              </div>
            )}
          </div>
        );
      })}
    </nav>
  );
};

export const TopBar = () => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [mini, setMini] = React.useState(() => readStore(STORE_MINI, false) === true);
  const [drawer, setDrawer] = React.useState(false);
  const loggedIn = !!user;

  // Desktop: content is shifted right of the fixed sidebar (CSS in index.css).
  React.useLayoutEffect(() => {
    if (!loggedIn) return undefined;
    const body = document.body;
    body.classList.add("app-shell");
    body.dataset.sidebar = mini ? "mini" : "full";
    return () => {
      body.classList.remove("app-shell");
      delete body.dataset.sidebar;
    };
  }, [loggedIn, mini]);

  // Close the phone drawer on navigation and with Escape.
  React.useEffect(() => setDrawer(false), [location.pathname]);
  React.useEffect(() => {
    if (!drawer) return undefined;
    const onKey = (e) => e.key === "Escape" && setDrawer(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [drawer]);

  if (!user || user === false) return null;

  const toggleMini = () => {
    setMini((m) => {
      writeStore(STORE_MINI, !m);
      return !m;
    });
  };

  return (
    <>
      <aside
        data-testid="sidebar"
        className={`no-print hidden lg:flex fixed inset-y-0 left-0 z-40 flex-col bg-soq-biru ${
          mini ? "w-16" : "w-60"
        }`}
      >
        <div className={`h-16 flex items-center border-b border-white/15 ${mini ? "justify-center" : "px-4"}`}>
          <Brand compact={mini} />
        </div>
        <div className="flex-1 overflow-y-auto px-2 py-3">
          <NavList role={user.role} user={user} pathname={location.pathname} mini={mini} />
        </div>
        <div className="border-t border-white/15 p-2 space-y-2">
          <button
            type="button"
            onClick={toggleMini}
            data-testid="sidebar-collapse"
            title={mini ? "Lebarkan menu" : "Ciutkan menu"}
            className="w-full flex items-center justify-center gap-2 rounded-md px-2 py-1.5 text-xs text-[#C9CEFF] hover:text-white hover:bg-white/10"
          >
            {mini ? <ChevronsRight size={16} /> : <><ChevronsLeft size={16} /> Ciutkan</>}
          </button>
          {!mini && <AppCredit warna="text-[#C9CEFF]" tautan />}
        </div>
      </aside>

      <header
        data-testid="top-bar"
        className="no-print w-full border-b border-slate-800 bg-white/95 backdrop-blur-md sticky top-0 z-30 px-4 sm:px-6 h-16 flex items-center justify-between gap-3"
      >
        <span aria-hidden className="absolute left-0 top-0 h-[5px] w-44 bg-soq-oranye [clip-path:polygon(0_0,100%_0,94%_100%,0_100%)]" />
        <div className="flex items-center gap-3 min-w-0">
          <button
            type="button"
            onClick={() => setDrawer(true)}
            data-testid="menu-open"
            aria-label="Buka menu"
            className="lg:hidden inline-flex items-center justify-center rounded-md border border-slate-700 p-2.5 text-soq-biru-tua hover:text-soq-biru"
          >
            <Menu size={18} />
          </button>
          <div className="lg:hidden min-w-0 rounded-lg bg-soq-biru px-2.5 py-1.5"><Logo varian="putih" className="h-6 w-auto" /></div>
        </div>

        <div className="flex items-center gap-3">
          {user.role === "admin" && <Lonceng />}
          {(user.role === "kasir" || user.role === "manager") && <Lonceng saya />}
          <div className="text-right hidden sm:block" data-testid="current-user-info">
            <span className="block text-sm text-slate-200 leading-none">{user.nama}</span>
            <span className="text-[10px] font-mono uppercase tracking-wider text-cyan-300" data-testid="current-user-role">
              {labelPeran(user)}
            </span>
          </div>
          {/* owner 2026-10-15 (A3): the role's guide + how to report a problem to Tim iartech */}
          <Link to={`/bantuan?dari=${encodeURIComponent(location.pathname)}`} data-testid="bantuan-link" title="Bantuan kendala" aria-label="Bantuan kendala"
            className="inline-flex items-center gap-1.5 rounded-lg border border-soq-oranye/70 bg-soq-oranye/10 px-2.5 py-1.5 text-xs font-semibold text-soq-biru-tua hover:bg-soq-oranye/20">
            <LifeBuoy size={14} /> <span className="hidden sm:inline">Bantuan</span>
          </Link>
          <Link to="/ganti-password" data-testid="ganti-password-link" title="Ganti password" aria-label="Ganti password"
            className="inline-flex items-center justify-center rounded-lg border border-slate-700 p-2 text-slate-400 hover:text-amber-300">
            <KeyRound size={14} />
          </Link>
          <button
            data-testid="logout-button"
            onClick={logout}
            className="inline-flex items-center gap-2 rounded-lg border border-slate-700 bg-slate-800/60 px-3 py-1.5 text-xs font-mono uppercase tracking-wider text-slate-300 transition-all duration-200 hover:border-red-500/40 hover:text-red-300"
          >
            <LogOut size={14} />
            <span className="hidden sm:inline">Logout</span>
          </button>
        </div>
      </header>

      {drawer && (
        <div className="no-print lg:hidden fixed inset-0 z-50" data-testid="menu-drawer" role="dialog" aria-modal="true" aria-label="Menu">
          <div className="absolute inset-0 bg-black/70" onClick={() => setDrawer(false)} data-testid="menu-backdrop" />
          <div className="absolute inset-y-0 left-0 w-72 max-w-[85vw] flex flex-col bg-soq-biru">
            <div className="h-16 flex items-center justify-between px-4 border-b border-white/15">
              <Brand />
              <button type="button" onClick={() => setDrawer(false)} data-testid="menu-close" aria-label="Tutup menu"
                className="rounded-md p-2.5 text-[#E8EBFF] hover:text-white">
                <X size={18} />
              </button>
            </div>
            <div className="px-4 py-2 border-b border-white/15 sm:hidden">
              <span className="block text-sm text-white">{user.nama}</span>
              <span className="text-[11px] font-semibold uppercase tracking-wider text-[#FFD08A]">{labelPeran(user)}</span>
            </div>
            <div className="flex-1 overflow-y-auto px-2 py-3">
              <NavList role={user.role} user={user} pathname={location.pathname} onNavigate={() => setDrawer(false)} />
            </div>
            <div className="border-t border-white/15 p-3"><AppCredit warna="text-[#C9CEFF]" tautan /></div>
          </div>
        </div>
      )}
    </>
  );
};
